import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, RefreshCw, X, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../App";

const API = process.env.REACT_APP_BACKEND_URL;

const MONTH_LABELS = {
  "01": "janvier", "02": "février", "03": "mars", "04": "avril",
  "05": "mai", "06": "juin", "07": "juillet", "08": "août",
  "09": "septembre", "10": "octobre", "11": "novembre", "12": "décembre",
};

const formatMonthLabel = (ym) => {
  if (!ym || ym.length < 7) return ym;
  const [year, month] = ym.split("-");
  return `${MONTH_LABELS[month] || month} ${year}`;
};

const formatCurrency = (v) => {
  if (v === null || v === undefined || Number.isNaN(v)) return "-";
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(v);
};

/**
 * Widget: trous de facturation détectés
 * Affiche les clients récurrents non facturés dans le mois courant.
 */
export default function BillingGapsWidget({ compact = false }) {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchAlerts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/billing-gaps`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAlerts(res.data.alerts || []);
    } catch (error) {
      // silent — juste pas de widget si error
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const res = await axios.post(`${API}/api/billing-gaps/refresh`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Détection lancée: ${res.data.detected} client(s) à vérifier`);
      await fetchAlerts();
    } catch (error) {
      toast.error("Erreur de rafraîchissement");
    } finally {
      setRefreshing(false);
    }
  };

  const handleDismiss = async (alertId, client) => {
    if (!window.confirm(`Ignorer l'alerte pour ${client} ? Elle ne sera plus affichée ce mois.`)) return;
    try {
      await axios.post(`${API}/api/billing-gaps/${alertId}/dismiss`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Alerte ignorée");
      setAlerts((prev) => prev.filter((a) => a.id !== alertId));
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleGoToClient = (client) => {
    navigate(`/factures?client=${encodeURIComponent(client)}`);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-4 animate-pulse" data-testid="billing-gaps-widget-loading">
        <div className="h-4 bg-slate-200 rounded w-1/3 mb-3"></div>
        <div className="h-16 bg-slate-100 rounded"></div>
      </div>
    );
  }

  if (alerts.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-emerald-200 p-4" data-testid="billing-gaps-widget-empty">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-semibold text-slate-800 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
            Facturation à jour
          </h3>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="text-slate-400 hover:text-slate-600"
            title="Relancer la détection"
            data-testid="billing-gaps-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          </button>
        </div>
        <p className="text-xs text-slate-500">Aucun trou de facturation détecté ce mois-ci.</p>
      </div>
    );
  }

  const totalEstimated = alerts.reduce((sum, a) => sum + (a.avg_amount_ttc || 0), 0);

  return (
    <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-200 p-4" data-testid="billing-gaps-widget">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-600" />
          <div>
            <h3 className="font-semibold text-slate-800">Trous de facturation détectés</h3>
            <p className="text-xs text-slate-500">
              {alerts.length} client{alerts.length > 1 ? "s" : ""} récurrent{alerts.length > 1 ? "s" : ""} non facturé{alerts.length > 1 ? "s" : ""} — potentiel {formatCurrency(totalEstimated)}
            </p>
          </div>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="text-slate-500 hover:text-slate-700 p-1"
          title="Relancer la détection"
          data-testid="billing-gaps-refresh"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
        </button>
      </div>

      <div className={`space-y-2 ${compact ? "max-h-56 overflow-y-auto" : ""}`}>
        {alerts.slice(0, compact ? 5 : 20).map((a) => (
          <div
            key={a.id}
            className="bg-white rounded-lg border border-amber-100 p-3 hover:border-amber-300 transition-colors"
            data-testid={`billing-gap-item-${a.client}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <button
                  onClick={() => handleGoToClient(a.client)}
                  className="font-medium text-slate-800 hover:text-amber-700 text-left truncate w-full flex items-center gap-1"
                >
                  {a.client}
                  <ChevronRight className="w-3 h-3 shrink-0" />
                </button>
                <div className="text-xs text-slate-600 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                  <span>Mois: <strong>{formatMonthLabel(a.month)}</strong></span>
                  <span>Jour habituel: <strong>{a.typical_day}</strong></span>
                  <span>Montant moyen: <strong>{formatCurrency(a.avg_amount_ttc)}</strong></span>
                </div>
                <div className="text-xs text-slate-400 mt-0.5">
                  Dernière facture: {a.last_invoice_date} ({formatCurrency(a.last_invoice_amount)}
                  {a.last_invoice_numero ? ` · ${a.last_invoice_numero}` : ""})
                </div>
              </div>
              <button
                onClick={() => handleDismiss(a.id, a.client)}
                className="text-slate-400 hover:text-red-500 p-1"
                title="Ignorer cette alerte"
                data-testid={`billing-gap-dismiss-${a.client}`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {compact && alerts.length > 5 && (
        <button
          onClick={() => navigate("/ventes/trous-facturation")}
          className="w-full mt-3 text-xs text-amber-700 hover:text-amber-900 font-medium"
          data-testid="billing-gaps-see-all"
        >
          Voir tous les trous ({alerts.length})
        </button>
      )}
    </div>
  );
}
